import { REASON_NAME, TOPIC_NAME, radioCheckRegEx } from './config'
import {
  FORM_ACTION,
  FORM_ALERT,
  FORM_FIELD,
  FORM_SELECTED_TOPIC,
  FORM_REASON_NAME,
  RESET_FORM_DATA,
  SET_FORM_STATE,
  SET_REASON_INPUTS
} from './mutations'

const alertDefaults = {
  actions: [],
  body: [],
  heading: '', // prop: { required: true, type: Null|String }
  type: 'info' // prop: { required: true, type: String, validator: value => ['success', 'warning', 'info', 'danger'] }
}
const initialFormState = {
  error: false,
  isValid: true,
  noValidate: true,
  pristine: true,
  submitting: false,
  submitted: false
}
export default {
  namespaced: true,
  state: {
    alert: {
      ...alertDefaults
    },
    formActions: {
      ReportContentConcern: {
        buttonEvent: {}
      }
    },
    formInputs: {
      topic: {
        disabled: null,
        valid: true,
        validationMessage: '',
        value: ''
      },
      reason: {
        disabled: null,
        valid: true,
        validationMessage: '',
        value: ''
      },
      additionalContext: {
        disabled: null,
        valid: true,
        validationMessage: '',
        value: ''
      },
      name: {
        disabled: null,
        valid: true,
        validationMessage: '',
        value: ''
      },
      email: {
        disabled: null,
        valid: true,
        validationMessage: '',
        value: ''
      },
      confirmation: {
        checked: false,
        disabled: null,
        valid: true,
        validationMessage: ''
      },
      partnerId: {
        type: 'hidden',
        valid: true,
        validationMessage: '',
        value: ''
      },
      partnerName: {
        type: 'hidden',
        valid: true,
        validationMessage: '',
        value: ''
      },
      identifier: {
        type: 'hidden',
        valid: true,
        validationMessage: '',
        value: ''
      },
      info: {
        type: 'hidden',
        valid: true,
        validationMessage: '',
        value: ''
      }
    },
    formState: {
      ...initialFormState
    },
    reasonName: null,
    reasonInputsList: [],
    selectedTopic: null
  },
  mutations: {
    [FORM_ALERT]: (state, alert) => {
      const stateAlertData = { ...alertDefaults, ...alert }
      state.alert = stateAlertData
    },
    [FORM_ACTION]: (state, action) => {
      const { name } = action
      const fieldData = { ...state.formActions[name], ...action }
      state.formActions[name] = fieldData
    },
    [FORM_FIELD]: (state, formField) => {
      const { name } = formField
      const fieldData = { ...state.formInputs[name], ...formField }
      state.formInputs[name] = fieldData
    },
    [RESET_FORM_DATA]: state => {
      const formFields = Object.keys(state.formInputs)
      formFields.forEach(field => {
        state.formInputs[field].value = ''
        state.formInputs[field].valid = true
        state.formInputs[field].validationMessage = ''
      })
    },
    [SET_FORM_STATE]: (state, newStateProps) => {
      const newFormState = { ...initialFormState, ...newStateProps }
      state.formState = newFormState
    },
    [FORM_SELECTED_TOPIC]: (state, selectedTopic) => {
      state.selectedTopic = selectedTopic
    },
    [FORM_REASON_NAME]: (state, reasonName) => {
      state.reasonName = reasonName
    },
    [SET_REASON_INPUTS]: (state, reasonInputsList) => {
      state.reasonInputsList = reasonInputsList
    }
  },
  actions: {
    /**
     * Updates state of the form alert message
     * @param {Object} context Vue context Object
     * @param {Object} alert see `alertDefaults` { type<String>, heading<String>>, text<Array>, actions<Array> }
     */
    setFormAlert({ commit }, alert) {
      commit(FORM_ALERT, alert)
    },
    /**
     * Reset Form Alert message (hide and set to type 'info')
     @param {Object} context Vue context Object
     */
    resetFormAlert(context) {
      context.commit(FORM_ALERT, alertDefaults)
    },
    /**
     @param {Object} context Vue context Object
     * @param {Object} action two required props: { name, data }
     */
    setFormAction({ commit }, action) {
      commit(FORM_ACTION, action)
    },
    /**
     @param {Object} context Vue context Object
     * @param {Object} fieldData two required props: { name, data }
     */
    setFormField({ commit }, fieldData) {
      commit(FORM_FIELD, fieldData)
    },
    /**
     @param {Object} context Vue context Object
     * @param {Object} name Selected Topic name (based on Reason)
     */
    setSelectedTopic({ commit }, name) {
      commit(FORM_SELECTED_TOPIC, name)
    },
    /**
     @param {Object} context Vue context Object
     * @param {Object} name Selected Reason name
     */
    setReasonName({ commit }, name) {
      commit(FORM_REASON_NAME, name)
    },
    /**
     * /!\ Looses reactivity if all replaced at once!
     * Hence individual commits
     @param {Object} context Vue context Object
     * @param {Object} inputs { name: {inputData} }
     */
    setFormInputs({ commit }, inputs) {
      const keys = Object.keys(inputs)
      keys.forEach(key => {
        commit(FORM_FIELD, inputs[key])
      })
    },
    /**
     * On Topic change -> Set Reason Select options
     * @param {Object} context { commit, getters } Vue context Object
     * @param {DOMElement} target input field
     */
    handleTopicChange({ commit, getters }, target) {
      const { name, value } = target
      const selectedTopic = value || null
      let reasonName = null
      let reasonData = {}

      if (name === TOPIC_NAME && getters.selectedTopic !== value) {
        // Set selected `reason`
        if (selectedTopic) {
          const reasonInput = getters.reasonInputByTopicValue(value)
          reasonName = reasonInput.name
          reasonData = {
            ...reasonInput,
            disabled: null,
            name: 'reason',
            type: 'dropdown',
            value: ''
          }
        } else {
          reasonData = {
            disabled: 'disabled',
            name: 'reason',
            type: 'dropdown',
            value: ''
          }
        }

        commit(FORM_FIELD, reasonData)
        commit(FORM_SELECTED_TOPIC, selectedTopic)
        commit(FORM_REASON_NAME, reasonName)
      }
    },
    /**
     * Takes a DOM Input element - target
     * Checks it's validity
     * Sets the Form Input state {value|checked, valid, validationMessage}
     * @param {Object} context { commit, getters } Vue context Object
     * @param {DOMElement} target DOM Element to validate
     * @returns
     */
    setInputValidity({ commit, getters }, target) {
      const { checked, type, value } = target
      const valid = target.checkValidity()
      const input = getters.formInputByName(target.name)
      let validationMessage = ''

      // Handle Validation
      if (valid === false) {
        // Match target.validity[TYPE] to `true` value
        switch (valid === false) {
          case 'required':
          case target.validity.valueMissing:
            validationMessage = input.requiredErrorText
            break
          case 'minLength':
          case target.validity.tooShort:
            validationMessage = input.minCharacterLengthErrorText
            break
          case 'maxLength':
          case target.validity.tooLong:
            validationMessage = input.maxCharacterLengthErrorText
            break
          case 'typeMismatch':
          case target.validity.typeMismatch:
          default:
            validationMessage = input.typeMismatchErrorText
            break
        }
      }

      const inputData = {
        ...input,
        valid,
        validationMessage
      }
      // `checked` for Radio | Checkbox
      if (type.match(radioCheckRegEx)) {
        inputData.checked = checked
      } else {
        inputData.value = value || null
      }
      commit(FORM_FIELD, inputData)
    },
    /**
     * Clear form data
     @param {Object} context Vue context Object
     */
    resetFormData({ commit }) {
      commit(RESET_FORM_DATA)
    },
    /**
     * Update form state with `newState`.
     * See `defaultFormState` props
     @param {Object} context Vue context Object
     * @param {Object} newState matching `defaultFormState` { isValid, noValidate, pristine, submitting, submitted }
     */
    setFormState({ commit }, newState) {
      commit(SET_FORM_STATE, newState)
    },
    /**
     * Update state with set of available `reason` select options
     @param {Object} context Vue context Object
     * @param {Object} reasonInputsList API Select inputs of type `reason-{TOPIC}`
     */
    setReasonInputsList({ commit }, reasonInputsList) {
      commit(SET_REASON_INPUTS, reasonInputsList)
    }
  },
  getters: {
    formActions(state) {
      return state.formActions
    },
    formAlert(state) {
      return state.alert
    },
    formInputs(state) {
      return state.formInputs
    },
    formInputByName(state) {
      return name => state.formInputs[name]
    },
    selectedTopic(state) {
      return state.selectedTopic
    },
    reasonName(state) {
      return state.reasonName
    },
    formState(state) {
      return state.formState
    },
    /**
     * Submit data from State Form Inputs
     * @param {Object} state Vue store
     * @returns Form POST Data Object {key: value}
     */
    getFormPostData(state) {
      const postData = {}
      const keys = Object.keys(state.formInputs)
      keys.forEach(key => {
        const {
          name, value, type, checked
        } = state.formInputs[key]
        postData[name] = type.match(radioCheckRegEx) ? checked : value
      })
      return postData
    },
    /**
     * Find matching Reason from Topic selection
     * @param {Object} state Vue state
     * @param {string} value topic select value
     * @returns Reason Select input with name === `reason-${value}`
     */
    reasonInputByTopicValue(state) {
      return value => state.reasonInputsList.find(select => select.name === `${REASON_NAME}-${value}`)
    }
  }
}
